import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Loading from '../Common/Loading';
import CustomAlert from '../Common/CustomAlert';
import englishBg from '../SYSADMIN/Support/Image/english_subject_bg.png';

interface Lesson {
    LessonID: string | number;
    StatusColor?: 'Grey' | 'Red' | 'Blue';
    WeekNumber: number;
    WeekEndDate: string;
    LsnNo: number;
    Country: string;
    DICFile: string;
    DICName: string;
    ChapterName: string;
    LessonCode: string;
    LessonName: string;
    Type: string;
    LessonTime: number;
    Added: string;
    TotAttempt: number;
    Shift: string;
    PrevAttempt: string;
    PrevScore: string;
    MDScore: string;
    Completed: boolean;
    NextLogic: string;
    PrevLogic: string;
}

interface StudentOption {
    Login: string;
    Name: string;
}

interface ApiData {
    weekly_minutes: number;
    weekly_minutes_source?: string;
    default_lesson_time?: number;
    lessons: Lesson[];
    current_week_end?: string;
    customer_time?: string;
    error?: string;
    student_name?: string;
    created_by?: string;
    created_date?: string;
    plan_created_on_key?: string;   // PIN — must be replayed verbatim on modify/delete
    source?: string;                // PIN
    current_week_number?: number;   // PIN
    plan_type?: string;
    curriculum?: string;
    grade?: string | number;
    competency?: string;
    family_students?: StudentOption[];
    wlp_status?: string;
    remaining_minutes?: number;
    remaining_weeks?: number;
}

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

const styles = {
    container: {
        fontFamily: 'T, Helvetica, sans-serif',
        fontSize: '12pt',
        minHeight: '100vh',
        width: '100%',
        margin: '0',
        padding: '0',
        backgroundImage: `url(${englishBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed',
        overflowY: 'auto' as const
    },
    subjectBanner: {
        height: '80px',
        width: '100%',
        backgroundColor: '#00549c',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'white',
        fontSize: '40px',
        fontWeight: 'bold',
        fontFamily: '"Times New Roman", Times, serif',
        letterSpacing: '1px',
        boxShadow: '0px 2px 5px rgba(0,0,0,0.3)'
    },
    contentPanel: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        margin: '10px 20px',
        borderRadius: '2px',
        border: '1px solid #ccc',
        color: 'white'
    },
    panelLabel: {
        color: 'white',
        fontSize: '12pt',
        fontFamily: 'Calibri, sans-serif',
        fontWeight: 'bold',
        paddingRight: '5px'
    },
    dataValue: {
        color: 'white',
        fontSize: '10pt',
        fontWeight: 'normal' as const,
    },
    textBox: {
        fontFamily: 'Times New Roman',
        fontSize: '10pt',
        border: '1px solid gray',
        backgroundColor: 'White',
        padding: '2px',
    },
    textBoxYellow: {
        fontFamily: 'Times New Roman',
        fontSize: '10pt',
        border: '1px solid black',
        backgroundColor: '#ffffcc',
        padding: '2px',
        width: '40px',
        textAlign: 'center' as const
    },
    button: {
        color: 'white',
        backgroundColor: '#ff6600',
        fontFamily: 'Times New Roman',
        fontSize: '10pt',
        height: '24px',
        padding: '5px 5px',
        cursor: 'pointer',
        border: 'none',
        margin: '2px',
        borderRadius: '5px'
    },
    tableHeader: {
        backgroundColor: '#5D7B9D',
        color: 'white',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        fontStyle: 'normal' as const,
        padding: '3px',
        border: '1px solid white',
        top: 0,
        zIndex: 1
    },
    gridBorder: {
        border: '1px solid #cccccc',
        borderCollapse: 'collapse' as const,
        width: '100%',
        backgroundColor: 'white'
    }
};

const EL_ViewLessonPlan: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [lessons, setLessons] = useState<Lesson[]>([]);
    const [weeklyMinutes, setWeeklyMinutes] = useState<string | number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
    const [currentWeekEnd, setCurrentWeekEnd] = useState<string>('');
    const [customerTime, setCustomerTime] = useState<string>('');

    // APPLIED subject — what the grid currently shows and what every
    // modify/delete/extend call acts on. Only changes when GO is clicked.
    const [subject, setSubject] = useState<string>(searchParams.get('Subject') || 'Mathematics');
    const [lastScoreFilter, setLastScoreFilter] = useState<string>('');
    const [lpFrom, setLpFrom] = useState<string>('');
    const [lpTo, setLpTo] = useState<string>('');
    const [familyStudents, setFamilyStudents] = useState<StudentOption[]>([]);
    const [alertInfo, setAlertInfo] = useState({ show: false, message: '' });
    const [weekNo, setWeekNo] = useState<string>('');

    // --- Pinned plan-generation fields (VB's hidden ViewState fields) ---
    // These are captured verbatim from the last successful GET and must be
    // replayed unchanged on every modify/delete POST. Never recompute these
    // client-side — that's what caused the earlier live-data incident.
    const [planCreatedOnKey, setPlanCreatedOnKey] = useState<string>('');
    const [planSource, setPlanSource] = useState<string>('');
    const [currentWeekNumber, setCurrentWeekNumber] = useState<number | null>(null);
    const [defaultLessonTime, setDefaultLessonTime] = useState<number>(60);
    const [previousWeeklyMinutes, setPreviousWeeklyMinutes] = useState<string | number>(0);
    const [wlpStatus, setWlpStatus] = useState<string>('');
    const [remainingWeeks, setRemainingWeeks] = useState<number>(0);

    const schoolCode = searchParams.get('SchoolCode') || searchParams.get('C') || '';
    const familyCode = searchParams.get('FamilyCode') || searchParams.get('F') || '';
    // APPLIED login (from the URL) — same rule as subject: only GO changes it.
    const login = searchParams.get('Login') || searchParams.get('L') || '';
    const currentUserName = sessionStorage.getItem('UserName') || 'System';

    // DRAFT values bound to the dropdowns. Changing these does NOT fetch.
    const [draftLogin, setDraftLogin] = useState<string>(login);
    const [draftSubject, setDraftSubject] = useState<string>(subject);

    const [metaData, setMetaData] = useState({
        studentName: '',
        createdBy: '',
        createdDate: '',
        planType: '',
        curriculum: '',
        grade: '',
        competency: ''
    });

    // --- Scroll reset support ---
    // The lessons grid lives in its own scroll container (and the page itself
    // can scroll too). When the applied student or subject changes, the new
    // plan must open at the top instead of inheriting the old scroll offset.
    const pageScrollRef = useRef<HTMLDivElement | null>(null);
    const gridScrollRef = useRef<HTMLDivElement | null>(null);
    // Set when the applied login/subject changes; consumed once the new
    // plan has been loaded and rendered. A plain refresh (GO with no change,
    // delete, extend, modify) leaves this false, so scroll position is kept.
    const pendingScrollResetRef = useRef<boolean>(false);

    const resetScrollToTop = () => {
        if (gridScrollRef.current) {
            gridScrollRef.current.scrollTop = 0;
            gridScrollRef.current.scrollLeft = 0;
        }
        if (pageScrollRef.current) {
            pageScrollRef.current.scrollTop = 0;
        }
        window.scrollTo(0, 0);
    };

    // Grey (past-week) rows are hidden, matching the old SysAdmin grid.
    const displayLessons = lessons.filter(l => l.StatusColor !== 'Grey');

    // Keep the dropdowns in sync if the applied values change from elsewhere
    // (e.g. the URL is edited or the page is re-entered with new params).
    useEffect(() => {
        setDraftLogin(login);
    }, [login]);

    useEffect(() => {
        setDraftSubject(subject);
    }, [subject]);

    // Applied student or subject changed: flag a scroll reset for when the new
    // data arrives, and reset right away so the old offset isn't carried over
    // while the previous rows are still on screen.
    useEffect(() => {
        pendingScrollResetRef.current = true;
        resetScrollToTop();
    }, [login, subject]);

    // Fetches only when the APPLIED values change (initial load, or after GO
    // commits new login/subject) — never from merely touching a dropdown.
    useEffect(() => {
        if (schoolCode && familyCode && login) {
            fetchLessonPlan();
        } else {
            setLoading(false);
        }
    }, [schoolCode, familyCode, login, subject, weekNo]);

    // GO: commit the draft dropdown values. If either changed, updating the
    // applied state triggers exactly ONE fetch via the effect above. If nothing
    // changed, GO acts as a plain refresh.
    const handleGo = () => {
        const loginChanged = draftLogin !== login;
        const subjectChanged = draftSubject !== subject;

        if (!loginChanged && !subjectChanged) {
            fetchLessonPlan();
            return;
        }

        // Selection belongs to the previous student/subject — drop it.
        setSelectedIds(new Set());

        if (subjectChanged) {
            setSubject(draftSubject);
        }
        if (loginChanged) {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('Login', draftLogin);
            setSearchParams(newParams);
        }
    };

    const sortLessons = (lessonsToSort: Lesson[]) => {
        return [...lessonsToSort].sort((a, b) => {
            if (a.LsnNo !== b.LsnNo) return a.LsnNo - b.LsnNo;
            const dateA = parseDdMmYyyy(a.WeekEndDate);
            const dateB = parseDdMmYyyy(b.WeekEndDate);
            return dateA - dateB;
        });
    };

    // Backend sends WeekEndDate as dd/MM/yyyy — `new Date()` misparses that
    // format, so parse it explicitly instead of relying on the JS Date ctor.
    const parseDdMmYyyy = (value: string): number => {
        if (!value) return 0;
        const [day, month, year] = value.split('/').map(Number);
        if (!day || !month || !year) return 0;
        return new Date(year, month - 1, day).getTime();
    };

    const fetchLessonPlan = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                SchoolCode: schoolCode,
                FamilyCode: familyCode,
                Login: login,
                Subject: subject,
                WeekNo: weekNo
            });

            const response = await fetch(`${API_BASE_URL}api/wlp/view_plan/?${params.toString()}`);
            const data: ApiData = await response.json();

            if (data.error) {
                setAlertInfo({ show: true, message: data.error });
            } else {
                setLessons(sortLessons(data.lessons || []));
                setWeeklyMinutes(data.weekly_minutes ?? 0);
                setCurrentWeekEnd(data.current_week_end ? new Date(data.current_week_end).toLocaleDateString('en-GB') : '');
                setCustomerTime(data.customer_time || new Date().toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
                setMetaData({
                    studentName: data.student_name || '',
                    createdBy: data.created_by || '',
                    createdDate: data.created_date || '',
                    planType: data.plan_type || '',
                    curriculum: data.curriculum || '',
                    // Grade 0 (Prep) is valid and must display as "0", as in VB.
                    // A truthiness check would blank it out.
                    grade: data.grade !== null && data.grade !== undefined && String(data.grade).trim() !== ''
                        ? String(data.grade)
                        : '',
                    competency: data.competency || ''
                });

                // Capture pinned fields for the NEXT modify/delete call.
                // These come only from the GET response — never computed here.
                setPlanCreatedOnKey(data.plan_created_on_key || '');
                setPlanSource(data.source || '');
                setCurrentWeekNumber(data.current_week_number ?? null);
                setDefaultLessonTime(data.default_lesson_time ?? 60);
                setPreviousWeeklyMinutes(data.weekly_minutes ?? 0);
                setWlpStatus(data.wlp_status || '');
                setRemainingWeeks(data.remaining_weeks ?? 0);

                if (data.family_students) {
                    setFamilyStudents(data.family_students);
                }

                // New student/subject loaded: once the new rows have rendered,
                // put the scroll bars back at the top.
                if (pendingScrollResetRef.current) {
                    pendingScrollResetRef.current = false;
                    requestAnimationFrame(() => {
                        resetScrollToTop();
                    });
                }
            }
        } catch (error) {
            setAlertInfo({ show: true, message: "Failed to fetch lesson plan" });
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLessons = () => {

        if (lessons.length === 0 || !planCreatedOnKey) {
            setAlertInfo({
                show: true,
                message: "There are no records to delete lessons or Update lessons"
            });
            return;
        }

        if (selectedIds.size === 0) {
            setAlertInfo({
                show: true,
                message: "Please select at least one lesson to delete."
            });
            return;
        }

        if (window.confirm("Are you sure you want to delete the selected lessons?")) {
            performUpdate(Array.from(selectedIds));
        }
    };

    const handleClose = () => {
        window.close();
    };

    const handleAddLessons = () => {
        if (selectedIds.size === 0) {
            setAlertInfo({ show: true, message: "Select a Lsn No to add the lessons" });
            return;
        }

        if (selectedIds.size > 1) {
            setAlertInfo({ show: true, message: "Please select only one lesson to define where to insert new lessons." });
            return;
        }

        const selectedLessonKey = Array.from(selectedIds)[0];
        const selectedLesson = lessons.find((l) => {
            const lessonIdMatches = String(l.LessonID) === String(selectedLessonKey);
            const lessonNoMatches = Number(l.LsnNo) === Number(selectedLessonKey);
            return lessonIdMatches || lessonNoMatches;
        });
        const lessonPosition = selectedLesson ? Number(selectedLesson.LsnNo) : 0;

        navigate('/SCHOOL/Teacher/EL_SetWorkPhase2', {
            state: {
                schoolCode: schoolCode,
                familyCode: familyCode,
                login: login,
                Work2FamilyCode: familyCode,
                Work2Login: login,

                studentName: metaData.studentName,
                gradeYear: metaData.grade,
                subject: subject,

                lessonPosition: lessonPosition,

                returnPath: window.location.pathname + window.location.search
            }
        });
    };

    const handleExtend = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}api/wlp/extend_plan/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    SchoolCode: schoolCode,
                    FamilyCode: familyCode,
                    Login: login,
                    Subject: subject,
                    ModifiedBy: currentUserName
                })
            });

            const result = await response.json();

            if (response.ok) {
                setAlertInfo({ show: true, message: result.message });
                await fetchLessonPlan();
            } else {
                setAlertInfo({ show: true, message: "Error: " + (result.error || result.message) });
            }
        } catch (error) {
            console.error("Extend failed", error);
            setAlertInfo({ show: true, message: "Network Error: Could not extend plan." });
        } finally {
            setLoading(false);
        }
    };

    const handleExtendSelective = async () => {
        if (window.confirm("Are you sure you want to Extend with Selective Lessons?")) {
            setLoading(true);
            try {
                const response = await fetch(`${API_BASE_URL}api/wlp/extend_selective_plan/`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        SchoolCode: schoolCode,
                        FamilyCode: familyCode,
                        Login: login,
                        Subject: subject,
                        Grade: metaData.grade,
                        ModifiedBy: currentUserName
                    })
                });

                const result = await response.json();

                if (response.ok) {
                    setAlertInfo({ show: true, message: result.message });
                    await fetchLessonPlan();
                } else {
                    setAlertInfo({ show: true, message: "Error: " + (result.error || result.message) });
                }
            } catch (error) {
                console.error("Extend Selective failed", error);
                setAlertInfo({ show: true, message: "Network Error: Could not extend plan." });
            } finally {
                setLoading(false);
            }
        }
    };

    const performUpdate = async (deleteIds: (string | number)[]) => {

        if (lessons.length === 0 || !planCreatedOnKey) {
            setAlertInfo({
                show: true,
                message: "There are no records to delete lessons or Update lessons"
            });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}api/wlp/modify_plan/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    SchoolCode: schoolCode,
                    FamilyCode: familyCode,
                    Login: login,
                    Subject: subject,
                    DeleteLessonIDs: deleteIds,
                    UpdatedWeeklyMinutes: weeklyMinutes,
                    ModifiedBy: currentUserName,

                    PlanCreatedOnKey: planCreatedOnKey,
                    Source: planSource,
                    CurrentWeekNumber: currentWeekNumber,
                    DefaultLessonTime: defaultLessonTime,
                    PreviousWeeklyMinutes: previousWeeklyMinutes,
                })
            });

            const result = await response.json();

            if (response.ok) {
                setAlertInfo({
                    show: true,
                    message: result.message
                });

                setSelectedIds(new Set());

                await fetchLessonPlan();

            } else {
                setAlertInfo({
                    show: true,
                    message: "Error: " + (result.error || result.message)
                });

                setLoading(false);
            }

        } catch (e) {
            console.error("Modify timing failed:", e);

            setAlertInfo({
                show: true,
                message: "Network Error"
            });

            setLoading(false);
        }
    };


    const handleDeleteLessonPlan = async () => {
        if (lessons.length === 0) {
            setAlertInfo({
                show: true,
                message: "There are no records to delete Lesson Plan"
            });
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}api/wlp/delete_plan/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    SchoolCode: schoolCode,
                    FamilyCode: familyCode,
                    Login: login,
                    Subject: subject,
                    ModifiedBy: currentUserName
                })
            });

            const result = await response.json();

            if (response.ok) {
                setSelectedIds(new Set());

                setAlertInfo({
                    show: true,
                    message: result.message || "Lesson Plan Deleted successfully."
                });

                await fetchLessonPlan();
            } else {
                setAlertInfo({
                    show: true,
                    message:
                        "Error: " +
                        (result.error ||
                            result.message ||
                            "Unable to delete Lesson Plan.")
                });

                setLoading(false);
            }
        } catch (error) {
            console.error("Delete Lesson Plan failed", error);

            setAlertInfo({
                show: true,
                message: "Network Error: Could not delete Lesson Plan."
            });

            setLoading(false);
        }
    };

    // Selection only ever touches rows that are visible in the grid (displayLessons).
    // Grey rows are hidden, and if they were ticked invisibly the next
    // "Delete Lessons" would delete rows the admin can't see.
    // All selection keys are numeric LsnNo values.
    const toLsnNo = (l: Lesson): number => Number(l.LsnNo);

    const handleGoScore = () => {
        // VB: an empty box means "select everything >= 80"
        const raw = String(lastScoreFilter ?? '').trim();
        const threshold = raw === '' ? 80 : parseInt(raw, 10);

        if (isNaN(threshold)) {
            setAlertInfo({ show: true, message: "Please enter a valid score." });
            return;
        }

        // VB unticks everything first, then ticks the matches
        const newSet = new Set<string | number>();
        displayLessons.forEach((l) => {
            const rawScore = l.PrevScore as unknown;
            if (rawScore === null || rawScore === undefined || String(rawScore).trim() === '') return;
            const score = Number(rawScore);
            if (!isNaN(score) && score >= threshold) {
                newSet.add(toLsnNo(l));
            }
        });

        setSelectedIds(newSet);
        setLastScoreFilter('');   // VB: txtLastScore.Text = ""

        if (newSet.size === 0) {
            setAlertInfo({
                show: true,
                message: `No lessons found with a previous score of ${threshold} or more.`
            });
        }
    };

    const handleGoRange = () => {
        const from = parseInt(String(lpFrom).trim(), 10);
        const to = parseInt(String(lpTo).trim(), 10);

        if (isNaN(from) || isNaN(to)) {
            setAlertInfo({ show: true, message: "Please enter both From and To lesson numbers." });
            return;
        }

        const lo = Math.min(from, to);
        const hi = Math.max(from, to);

        // VB unticks everything first, then ticks the range
        const newSet = new Set<string | number>();
        displayLessons.forEach((l) => {
            const n = toLsnNo(l);
            if (!isNaN(n) && n >= lo && n <= hi) {
                newSet.add(n);
            }
        });

        setSelectedIds(newSet);
        setLpFrom('');   // VB clears both boxes
        setLpTo('');
    };

    const handleClear = () => {
        setSelectedIds(new Set());
    };

    const handleCheckboxChange = React.useCallback((id: string | number) => {
        setSelectedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
                newSet.delete(id);
            } else {
                newSet.add(id);
            }
            return newSet;
        });
    }, []);

    const handleViewLog = () => {
        const width = 520;
        const height = 300;
        const left = (window.screen.width / 2) - (width / 2);
        const top = (window.screen.height / 2) - (height / 2);

        const url = `/SYSADMIN/Support/WLPModifiedLog?SchoolCode=${encodeURIComponent(schoolCode)}&FamilyCode=${encodeURIComponent(familyCode)}&Login=${encodeURIComponent(login)}&Subject=${encodeURIComponent(subject)}`;

        const features = `toolbar=no,location=no,directories=no,status=yes,menubar=no,scrollbars=yes,resizable=yes,copyhistory=no,width=${width},height=${height},top=${top},left=${left}`;

        window.open(url, 'WLPModifiedLog', features);
    };

    if (!schoolCode) return <div style={{ padding: '20px', backgroundColor: 'white' }}>Missing Parameters</div>;

    return (
        <div style={styles.container} ref={pageScrollRef}>
            {loading && !alertInfo.show && (
                <div>
                    <Loading />
                </div>
            )}

            <div className="" style={styles.subjectBanner}>
                VIEW LESSON PLAN
            </div>

            <div style={{ display: 'flex', alignItems: 'center', margin: '16px', padding: '4px', color: 'white', fontFamily: 'Open Sans, sans-serif' }}>
                <div>
                    <span style={styles.panelLabel}>SchoolCode:</span>
                    <span style={styles.dataValue}>{schoolCode}</span>
                    <span style={styles.panelLabel}>| FamilyCode:</span>
                    <span style={styles.dataValue}>{familyCode}</span>
                    <span style={styles.panelLabel}>| Login:</span>
                    <select
                        style={{ ...styles.textBox, width: '100px' }}
                        value={draftLogin}
                        onChange={(e) => setDraftLogin(e.target.value)}
                    >
                        {familyStudents.length > 0 ? (
                            familyStudents.map((student) => (
                                <option key={student.Login} value={student.Login}>
                                    {student.Name}
                                </option>
                            ))
                        ) : (
                            <option value={login}>{login}</option>
                        )}
                    </select>
                </div>
                <div>
                    <span style={styles.panelLabel}>| Subject:</span>
                    <select
                        style={{ ...styles.textBox, width: '120px' }}
                        value={draftSubject}
                        onChange={(e) => setDraftSubject(e.target.value)}
                    >
                        <option value="Mathematics">Mathematics</option>
                        <option value="English">English</option>
                    </select>
                    &nbsp;&nbsp;

                    <button type="button" style={styles.button} onClick={handleGo}>GO</button>
                    &nbsp;&nbsp;|&nbsp;&nbsp;

                    <span style={styles.panelLabel}>Weekly Minutes:</span>
                    <input
                        type="text"
                        style={styles.textBoxYellow}
                        value={weeklyMinutes}
                        onChange={(e) => setWeeklyMinutes(e.target.value)}
                    />
                    <button type="button" style={styles.button} onClick={() => performUpdate([])}>Modify Timing</button>
                    <button
                        type="button"
                        style={styles.button}
                        onClick={handleDeleteLessonPlan}
                    >
                        Delete Lesson Plan
                    </button>
                    {wlpStatus && (
                        <span style={{ ...styles.panelLabel, color: '#ffcc00', marginLeft: '8px' }}>{wlpStatus}</span>
                    )}
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', padding: '4px', margin: '16px', color: 'white' }}>
                <div>
                    <span style={styles.panelLabel}>Customer Date/Time:</span>
                    <span style={styles.dataValue}>{customerTime}</span>
                    <span style={styles.panelLabel}>| Week ending:</span>
                    <span style={styles.dataValue}>{currentWeekEnd}</span>
                    <span style={styles.panelLabel}>| WLP will last:</span>
                    <span style={styles.dataValue}>{remainingWeeks} week(s)</span>
                </div>
                <div>
                    <span style={styles.panelLabel}>Select Last Score {'>'}= 80:</span>
                    <input type="text" style={{ ...styles.textBox, width: '30px' }} value={lastScoreFilter} onChange={e => setLastScoreFilter(e.target.value)} />
                    &nbsp;&nbsp;
                    <button type="button" style={styles.button} onClick={handleGoScore}>Go</button>
                    &nbsp;&nbsp;
                    <span style={styles.panelLabel}>Select LP:</span>
                    <input type="text" style={{ ...styles.textBox, width: '40px' }} value={lpFrom} onChange={e => setLpFrom(e.target.value)} />
                    <span style={styles.panelLabel}>To:</span>
                    <input type="text" style={{ ...styles.textBox, width: '40px' }} value={lpTo} onChange={e => setLpTo(e.target.value)} />
                    &nbsp;
                    <button type="button" style={styles.button} onClick={handleGoRange}>Go</button>
                    <button type="button" style={styles.button} onClick={handleClear}>Clear checkbox</button>
                </div>
            </div>
            <div style={styles.contentPanel}>
                <div style={{ backgroundColor: '#5D7B9D', color: 'white', padding: '5px', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: '12pt', border: '1px solid #cccccc', borderBottom: 'none' }}>
                    <span style={{ fontWeight: 'bold' }}> Student:</span> <span>{metaData.studentName}</span> &nbsp;|&nbsp;
                    <span style={{ fontWeight: 'bold' }}>LP Created By:</span> <span>{metaData.createdBy}</span> &nbsp;|&nbsp;
                    <span style={{ fontWeight: 'bold' }}>Created:</span> <span>{metaData.createdDate}</span> , using <span>{metaData.planType}</span> &nbsp;|&nbsp;
                    <span style={{ fontWeight: 'bold' }}>Curriculum:</span> <span>{metaData.curriculum}</span> &nbsp;|&nbsp;
                    <span style={{ fontWeight: 'bold' }}>Grade:</span> <span>{metaData.grade}</span> &nbsp;|&nbsp;
                    <span style={{ fontWeight: 'bold' }}>Competency:</span> <span>{metaData.competency}</span> &nbsp;&nbsp;
                    <span style={{ color: 'white', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'normal' }} onClick={handleViewLog}>
                        View Log
                    </span>
                </div>

                <div
                    ref={gridScrollRef}
                    style={{
                        overflowX: 'auto',
                        overflowY: 'auto',
                        maxHeight: 'calc(100vh - 300px)',
                        width: '100%'
                    }}
                >
                    <table style={styles.gridBorder} cellPadding={3} cellSpacing={0}>
                        <thead>
                            <tr style={{ height: '24px', fontSize: '10pt' }}>
                                <th style={{ ...styles.tableHeader, width: '30px', textAlign: 'center' }}></th>
                                <th style={styles.tableHeader}>Wk.No.</th>
                                <th style={styles.tableHeader}>Week Ending</th>
                                <th style={styles.tableHeader}>Lsn No.</th>
                                <th style={styles.tableHeader}>Country</th>
                                <th style={styles.tableHeader}>DIC File</th>
                                <th style={styles.tableHeader}>DIC Name</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'left' }}>Chapter Name</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'left' }}>Lsn Code</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'left' }}>Lsn Name</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'left' }}>Type</th>
                                <th style={styles.tableHeader}>Lsn Time</th>
                                <th style={{ ...styles.tableHeader, textAlign: 'left' }}>Added</th>
                                <th style={styles.tableHeader}>Tot.Attempt</th>
                                <th style={styles.tableHeader}>Shift</th>
                                <th style={styles.tableHeader}>Prev.Attempt</th>
                                <th style={styles.tableHeader}>Prev.Score</th>
                                <th style={styles.tableHeader}>MD.Score</th>
                                <th style={styles.tableHeader}>Completed</th>
                                <th style={styles.tableHeader}>NextLogic</th>
                                <th style={styles.tableHeader}>Prev.Logic</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayLessons.map((lesson) => (
                                <LessonRow
                                    key={lesson.LsnNo}
                                    lesson={lesson}
                                    isSelected={selectedIds.has(Number(lesson.LsnNo))}
                                    onCheck={handleCheckboxChange}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px' }}>
                <div>
                    <button type="button" style={styles.button} onClick={handleDeleteLessons}>Delete Lessons</button>
                    &nbsp;&nbsp;
                    <button type="button" style={styles.button} onClick={handleClose}>CLOSE</button>
                </div>
                <div>
                    <button type="button" style={{ ...styles.button }}
                        onClick={handleAddLessons}
                    >Add Lessons</button>
                    &nbsp;&nbsp;
                    <button type="button" style={styles.button} onClick={handleExtend}>Extend</button>
                    &nbsp;&nbsp;
                    <button type="button" style={styles.button} onClick={handleExtendSelective}>Extend SelectiveLSns</button>
                </div>
            </div>
            {alertInfo.show && (
                <CustomAlert
                    message={alertInfo.message}
                    onClose={() => setAlertInfo({ ...alertInfo, show: false })}
                />
            )}
        </div>
    );
}

const getRowStyle = (color?: string) => {
    const base = { fontFamily: 'Times New Roman', fontSize: '11pt', padding: '3px' };
    if (color === 'Grey') return { ...base, color: '#666666', backgroundColor: '#F0F0F0' };
    if (color === 'Red') return { ...base, color: 'Red' };
    return { ...base, color: 'Blue' };
};

const LessonRow = React.memo(({ lesson, isSelected, onCheck }: {
    lesson: Lesson,
    isSelected: boolean,
    onCheck: (id: string | number) => void
}) => {
    const borderColor = lesson.StatusColor === 'Red' ? 'red' : lesson.StatusColor === 'Grey' ? '#999' : 'blue';
    const cellBorder = { borderBottom: `1px solid ${borderColor}`, borderRight: `1px solid ${borderColor}` };
    return (
        <tr style={getRowStyle(lesson.StatusColor)}>
            <td style={{ ...cellBorder, borderLeft: `1px solid ${borderColor}`, textAlign: 'center' }}>
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onCheck(Number(lesson.LsnNo))}
                />
            </td>
            <td style={{ ...cellBorder, textAlign: 'left', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.WeekNumber}</td>
            <td style={{ ...cellBorder, textAlign: 'left', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.WeekEndDate}</td>
            <td style={{ ...cellBorder, textAlign: 'center', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.LsnNo}</td>
            <td style={{ ...cellBorder, fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.Country}</td>
            <td style={{ ...cellBorder, fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.DICFile}</td>
            <td style={{ ...cellBorder, whiteSpace: 'nowrap', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.DICName}</td>
            <td style={{ ...cellBorder, whiteSpace: 'nowrap', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.ChapterName}</td>
            <td style={{ ...cellBorder, whiteSpace: 'nowrap', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.LessonCode}</td>
            <td style={{ ...cellBorder, whiteSpace: 'nowrap', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.LessonName}</td>
            <td style={{ ...cellBorder, fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.Type ? lesson.Type === 'L' ? 'Lesson' : lesson.Type === 'G' ? 'Game' : 'Test' : ''}</td>
            <td style={{ ...cellBorder, textAlign: 'center', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.LessonTime}</td>
            <td style={{ ...cellBorder, whiteSpace: 'nowrap', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.Added}</td>
            <td style={{ ...cellBorder, textAlign: 'center', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.TotAttempt}</td>
            <td style={{ ...cellBorder, fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.Shift}</td>
            <td style={{ ...cellBorder, fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.PrevAttempt}</td>
            <td style={{ ...cellBorder, textAlign: 'center', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.PrevScore}</td>
            <td style={{ ...cellBorder, textAlign: 'center', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>{lesson.MDScore}</td>
            <td style={{ ...cellBorder, textAlign: 'center', fontStyle: 'normal', fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 'small' }}>
                {lesson.Completed ? <span style={{ color: 'green', fontWeight: 'bold' }}>C</span> : ''}
            </td>
            <td style={{ ...cellBorder, fontStyle: 'normal' }}>{lesson.NextLogic}</td>
            <td style={{ ...cellBorder, fontStyle: 'normal' }}>{lesson.PrevLogic}</td>
        </tr>
    );
}
);

export default EL_ViewLessonPlan;